import { createAnalyticsUrl, restGet } from '~/src/dash/api/rest';
import { TKey } from '~/src/dash/publicaUI';
import { IReportDataRow } from '~/src/dash/views/reporting/Preview/common';

export interface ICalendarSetting {
  MaxDaysLookBack: number;
  MaxDaysPerQuery: number;
  MinDaysLookBack: number;
}

export interface IReportFilter {
  DimensionKey: string;
  IsHideInactiveEnabled: boolean;
  IsIDDisplayed: boolean;
  IsIncludeExcludeEnabled: boolean;
  IsSelectAllEnabled: boolean;
  Key: string;
  Label: string;
  Type: string; // input, list
  MaxSelectedItems: number;
}

export interface IReportMetric {
  IsSelectedByDefault: boolean;
  IsSelectedRequired: boolean;
  Key: keyof IReportDataRow;
  Label: string;
  Prefix: string;
  SortByType: string;
  Subheader: string;
  Suffix: string;
}

export interface IReportDimension {
  IsSelectedByDefault: boolean;
  IsSelectedRequired: boolean;
  Key: keyof IReportDataRow;
  Label: string;
  SortByType: string;
  Subheader: string;
}

export interface IReportType {
  CalendarSetting: ICalendarSetting;
  Category: number;
  Currency: number;
  Description: string;
  Dimensions: IReportDimension[];
  EnableCalendarSetting: boolean;
  EnableCurrency: boolean;
  EnableTimezone: boolean;
  ExtendedGraphID: number;
  Filters: IReportFilter[];
  GraphID: number;
  ID: ReportTypeID;
  Label: string;
  Metrics: IReportMetric[];
  Timezone: string;
  IsIASReport: boolean;
}

// interface used specific for Report Preview
export interface IReportTypePreview {
  CalendarSetting: ICalendarSetting;
  Category: number;
  Currency: number;
  Description: string;
  Dimensions: Record<string, IReportDimension>;
  EnableCalendarSetting: boolean;
  EnableCurrency: boolean;
  EnableTimezone: boolean;
  ExtendedGraphID: number;
  Filters: Record<string, IReportFilter>;
  GraphID: number;
  ID: ReportTypeID;
  Label: string;
  Metrics: Record<string, IReportMetric>;
  Timezone: string;
}

// this matches exactly to BE
export enum ReportTypeID {
  UnknownReport = 0,
  AuctionReport = 2,
  BidderReport = 3,
  SSPReport = 4,
  CampaignReport = 5,
  AudienceSegmentLiverampReport = 6,
  SSAIReport = 7,
  UniqueUserReport = 8,
  AudienceSegmentForAllPublishersReport = 9,
  AuctionFillRateReport = 10,
  BrandSafetyTrackerReport = 13,
  DealReport = 14,
  OutboundDealReport = 15,
  InventoryRouteReport = 17,
  DestinationReport = 18,
  ContextualSegmentReport = 19,
  IASThirdPartyVASTTrackingReport = 20,
  IASFirstPartyVASTTrackingReport = 21,
  CreativeValidationReport = 22,
  EleaAIScanAllReport = 23,
  EleaAIEnrichmentReport = 24,
  TTDReport = 28,
  CreativeReviewReport = 29,
  ShareOfVoiceReport = 30,
  PodHourlyReport = 31,
  AdvertiserRuleReport = 32,
  APSReport = 33,
  ThirdPartyTrackerDomainReport = 34,
  EleaAIEnrichmentBillingReport = 35,
  EleaAILanguageDetectionReport = 36,
  BidFloorSettingReport = 37,
  BidSaverTrueLiftReport = 38,
  SSAIUserActivityReport = 45,
  AuctionQuickReport = 46,
  CampaignQuickReport = 47,
  BidderQuickReport = 48,
  AdomainQuickReport = 49,
  ContentQuickReport = 50,
  AppBundleQuickReport = 51,
  IASContentLevelPerfomanceReport = 52,
  SmartFloorsReport = 53,
  TrafficShapingReport = 54,
  PublicaVaultJSONTrueLiftReport = 55,
  ReachAndFrequencyReport = 56,
}

// convert string number to reportTypeID
export const mapStringToReportTypeID: { [key: string]: ReportTypeID } = {
  '0': ReportTypeID.UnknownReport,
  '2': ReportTypeID.AuctionReport,
  '3': ReportTypeID.BidderReport,
  '4': ReportTypeID.SSPReport,
  '5': ReportTypeID.CampaignReport,
  '6': ReportTypeID.AudienceSegmentLiverampReport,
  '7': ReportTypeID.SSAIReport,
  '8': ReportTypeID.UniqueUserReport,
  '9': ReportTypeID.AudienceSegmentForAllPublishersReport,
  '10': ReportTypeID.AuctionFillRateReport,
  '13': ReportTypeID.BrandSafetyTrackerReport,
  '14': ReportTypeID.DealReport,
  '15': ReportTypeID.OutboundDealReport,
  '17': ReportTypeID.InventoryRouteReport,
  '18': ReportTypeID.DestinationReport,
  '19': ReportTypeID.ContextualSegmentReport,
  '20': ReportTypeID.IASThirdPartyVASTTrackingReport,
  '21': ReportTypeID.IASFirstPartyVASTTrackingReport,
  '22': ReportTypeID.CreativeValidationReport,
  '23': ReportTypeID.EleaAIScanAllReport,
  '24': ReportTypeID.EleaAIEnrichmentReport,
  '28': ReportTypeID.TTDReport,
  '29': ReportTypeID.CreativeReviewReport,
  '30': ReportTypeID.ShareOfVoiceReport,
  '31': ReportTypeID.PodHourlyReport,
  '32': ReportTypeID.AdvertiserRuleReport,
  '33': ReportTypeID.APSReport,
  '34': ReportTypeID.ThirdPartyTrackerDomainReport,
  '35': ReportTypeID.EleaAIEnrichmentBillingReport,
  '36': ReportTypeID.EleaAILanguageDetectionReport,
  '37': ReportTypeID.BidFloorSettingReport,
  '38': ReportTypeID.BidSaverTrueLiftReport,
  '45': ReportTypeID.SSAIUserActivityReport,
  '46': ReportTypeID.AuctionQuickReport,
  '47': ReportTypeID.CampaignQuickReport,
  '48': ReportTypeID.BidderQuickReport,
  '49': ReportTypeID.AdomainQuickReport,
  '50': ReportTypeID.ContentQuickReport,
  '51': ReportTypeID.AppBundleQuickReport,
  '52': ReportTypeID.IASContentLevelPerfomanceReport,
  '53': ReportTypeID.SmartFloorsReport,
  '54': ReportTypeID.TrafficShapingReport,
  '55': ReportTypeID.PublicaVaultJSONTrueLiftReport,
  '56': ReportTypeID.ReachAndFrequencyReport,
};

export const reportTypeIDToTKey: { [key: string]: TKey } = {
  '0': 'UnknownReport',
  '2': 'AuctionReport',
  '3': 'BidderReport',
  '4': 'SSPReport',
  '5': 'CampaignReport',
  '6': 'AudienceSegmentLiverampReport',
  '7': 'SSAIReport',
  '8': 'UniqueUserReport',
  '9': 'AudienceSegmentForAllPublishersReport',
  '10': 'AuctionFillRateReport',
  '13': 'BrandSafetyTrackerReport',
  '14': 'DealReport',
  '15': 'OutboundDealReport',
  '17': 'InventoryRouteReport',
  '18': 'DestinationReport',
  '19': 'ContextualSegmentReport',
  '20': 'IASThirdPartyVASTTrackingReport',
  '21': 'IASFirstPartyVASTTrackingReport',
  '22': 'CreativeValidationReport',
  '23': 'EleaAIScanAllReport',
  '24': 'EleaAIEnrichmentReport',
  '28': 'TTDReport',
  '29': 'CreativeReviewReport',
  '30': 'ShareOfVoiceReport',
  '31': 'PodHourlyReport',
  '32': 'AdvertiserRuleReport',
  '33': 'APSReport',
  '34': 'ThirdPartyTrackerDomainReport',
  '35': 'EleaAIEnrichmentBillingReport',
  '36': 'EleaAILanguageDetectionReport',
  '37': 'BidFloorSettingReport',
  '38': 'PublicaVaultTrueLiftReport',
  '45': 'SSAIUserActivityReport',
  '46': 'AuctionQuickReport',
  '47': 'CampaignQuickReport',
  '48': 'BidderQuickReport',
  '49': 'AdomainQuickReport',
  '50': 'ContentQuickReport',
  '51': 'AppBundleQuickReport',
  '52': 'IASContentLevelPerformanceReport',
  '53': 'SmartFloorsReport',
  '54': 'TrafficShapingReport',
  '55': 'PublicaVaultJSONTrueLiftReport',
  '56': 'ReachAndFrequencyReport',
};

export const reportTypeIDToDescriptionTKey: { [key: string]: TKey } = {
  '46': 'AuctionQuickReportDescription',
  '47': 'CampaignQuickReportDescription',
  '48': 'BidderQuickReportDescription',
  '49': 'AdomainQuickReportDescription',
  '50': 'ContentQuickReportDescription',
  '51': 'AppBundleQuickReportDescription',
};

export const reportTypeIDToPillTKey: { [key: string]: TKey } = {
  '46': 'AuctionQuickReportPill',
  '47': 'CampaignQuickReportPill',
  '48': 'BidderQuickReportPill',
  '49': 'AdomainQuickReportPill',
  '50': 'ContentQuickReportPill',
  '51': 'AppBundleQuickReportPill',
};

interface IExcludedReportingDimensionItem {
  key: string;
  text: string;
}

/*
The items here only affects what shows up in the UI dropdown. The keys and labels must
match the backend's keys and labels stored in dimensions.go.
*/
export function getExcludedReportingDimensionItems(): IExcludedReportingDimensionItem[] {
  return [
    {
      key: 'category',
      text: 'Category',
    },
    {
      key: 'content_dist_id',
      text: 'Content Dist. ID',
    },
    {
      key: 'content_id',
      text: 'Content ID',
    },
    {
      key: 'content_keywords',
      text: 'Content Keywords',
    },
    {
      key: 'content_producer_id',
      text: 'Content Producer ID',
    },
    {
      key: 'custom1',
      text: 'Custom1',
    },
    {
      key: 'custom4',
      text: 'Custom4',
    },
    {
      key: 'custom5',
      text: 'Custom5',
    },
    {
      key: 'custom6',
      text: 'Custom6',
    },
    {
      key: 'custom7',
      text: 'Custom7',
    },
    {
      key: 'custom8',
      text: 'Custom8',
    },
    {
      key: 'custom9',
      text: 'Custom9',
    },
    {
      key: 'custom10',
      text: 'Custom10',
    },
    {
      key: 'custom11',
      text: 'Custom11',
    },
    {
      key: 'custom12',
      text: 'Custom12',
    },
    {
      key: 'custom13',
      text: 'Custom13',
    },
    {
      key: 'custom14',
      text: 'Custom14',
    },
    {
      key: 'custom15',
      text: 'Custom15',
    },
    {
      key: 'custom16',
      text: 'Custom16',
    },
    {
      key: 'custom17',
      text: 'Custom17',
    },
    {
      key: 'custom18',
      text: 'Custom18',
    },
    {
      key: 'fw_section_id_v2',
      text: 'Freewheel Section ID',
    },
    {
      key: 'fw_section_name',
      text: 'Freewheel Section Name',
    },
    {
      key: 'iris_context',
      text: 'Iris Context',
    },
    {
      key: 'iris_id',
      text: 'Iris ID',
    },
    {
      key: 'ortb_site_name',
      text: 'Site Name',
    },
  ];
}

// check if a reportType is an IAS Report
export function isIASReportType(reportType: number): boolean {
  return [
    ReportTypeID.IASThirdPartyVASTTrackingReport,
    ReportTypeID.IASFirstPartyVASTTrackingReport,
  ].includes(reportType);
}

// retrieve a list of reportTypes that is available for the publisher and user
export async function getReportTypes(
  publisherID: string,
  isReportPreview: boolean = false,
): Promise<IReportType[]> {
  const reqUrl =
    createAnalyticsUrl('report_types', publisherID) + `&is_report_preview=${isReportPreview}`;

  try {
    const response = await restGet<any>(reqUrl);
    return response?.parsedBody?.ReportTypes || [];
  } catch (err) {
    throw err;
  }
}

// retrieve a single reportType
export async function getReportType(
  publisherID: string,
  reportTypeID: ReportTypeID,
  isReportPreview: boolean = false,
): Promise<IReportType> {
  const reqUrl =
    createAnalyticsUrl(`report_type/${reportTypeID}`, publisherID, 'v1') +
    `&is_report_preview=${isReportPreview}`;
  try {
    const response = await restGet<any>(reqUrl);
    return response?.parsedBody?.ReportType || {};
  } catch (err) {
    throw err;
  }
}
